import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BUSINESS_INFO } from '../../mocks/lisaMerkleData';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Location', path: '/location' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [topBarVisible, setTopBarVisible] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      setTopBarVisible(y < 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      {/* Top utility bar */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-charcoal transition-all duration-500 ${
          topBarVisible ? 'h-9 opacity-100' : 'h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-8 h-full flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a
              href={BUSINESS_INFO.phoneHref}
              className="flex items-center gap-1.5 text-cream/60 text-[11px] tracking-wider font-sans hover:text-stone transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-phone-line text-stone text-[11px]" />
              </span>
              {BUSINESS_INFO.phone}
            </a>
            <span className="hidden sm:block w-px h-3 bg-cream/10" />
            <a
              href={`mailto:${BUSINESS_INFO.email}`}
              className="hidden sm:flex items-center gap-1.5 text-cream/60 text-[11px] tracking-wider font-sans hover:text-stone transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-mail-line text-stone text-[11px]" />
              </span>
              {BUSINESS_INFO.email}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={BUSINESS_INFO.instagram}
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label="Instagram"
              className="w-5 h-5 flex items-center justify-center text-cream/40 hover:text-stone transition-colors cursor-pointer"
            >
              <i className="ri-instagram-line text-xs" />
            </a>
            <a
              href={BUSINESS_INFO.facebook}
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label="Facebook"
              className="w-5 h-5 flex items-center justify-center text-cream/40 hover:text-stone transition-colors cursor-pointer"
            >
              <i className="ri-facebook-fill text-xs" />
            </a>
            <span className="text-cream/20 text-[11px] tracking-wider font-sans hidden md:block">Beverly Hills · Los Angeles · Nationwide</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
          topBarVisible ? 'top-9' : 'top-0'
        } ${
          scrolled
            ? 'bg-cream/98 backdrop-blur-sm border-b border-stone/15 shadow-none'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 cursor-pointer" aria-label="Lisa Merkle Design Home">
            <img
              src={BUSINESS_INFO.logoUrl}
              alt="Lisa Merkle Design"
              className={`h-11 w-auto object-contain transition-all duration-500 ${
                scrolled ? 'brightness-0' : 'brightness-0 invert'
              }`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-[11px] tracking-[0.2em] uppercase font-sans font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap group ${
                  location.pathname === link.path
                    ? scrolled ? 'text-charcoal' : 'text-white'
                    : scrolled
                    ? 'text-charcoal/55 hover:text-charcoal'
                    : 'text-white/65 hover:text-white'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className={`absolute -bottom-1 left-0 right-0 h-px transition-colors ${scrolled ? 'bg-stone' : 'bg-white/50'}`} />
                )}
              </Link>
            ))}
          </nav>

          {/* Right Side CTA */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href={BUSINESS_INFO.phoneHref}
              className={`flex items-center gap-2 text-[11px] font-sans font-medium tracking-widest transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-charcoal/60 hover:text-charcoal' : 'text-white/65 hover:text-white'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-phone-line text-xs ${scrolled ? 'text-stone-dark' : 'text-stone'}`} />
              </span>
              {BUSINESS_INFO.phone}
            </a>
            <Link
              to="/location#contact"
              className={`px-7 py-2.5 text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full transition-all duration-400 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'bg-charcoal text-cream hover:bg-charcoal/85 border border-charcoal'
                  : 'bg-white/12 text-white border border-white/35 hover:bg-white/20'
              }`}
            >
              Schedule Consultation
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`lg:hidden flex flex-col gap-[5px] p-2 cursor-pointer transition-colors ${
              scrolled ? 'text-charcoal' : 'text-white'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-px bg-current transition-all duration-300 origin-center ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`block w-4 h-px bg-current transition-all duration-300 ${menuOpen ? 'opacity-0 w-0' : ''}`} />
            <span className={`block w-6 h-px bg-current transition-all duration-300 origin-center ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-600 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-charcoal" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(https://lisamerkledesign.com/wp-content/uploads/2022/12/185378651_3974479969308369_7407879718005515588_n.jpg)` }}
        />
        <div className="relative h-full flex flex-col pt-28 pb-12 px-10">
          <nav className="flex flex-col gap-7">
            {NAV_LINKS.map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-display text-4xl sm:text-5xl font-light leading-tight cursor-pointer transition-colors ${
                  location.pathname === link.path ? 'text-cream' : 'text-cream/60 hover:text-cream'
                }`}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="mt-auto flex flex-col gap-3 border-t border-cream/10 pt-8">
            <a href={BUSINESS_INFO.phoneHref} className="flex items-center gap-3 text-cream/70 hover:text-stone transition-colors cursor-pointer">
              <i className="ri-phone-line text-stone" />
              <span className="font-sans text-base tracking-wide">{BUSINESS_INFO.phone}</span>
            </a>
            <a href={`mailto:${BUSINESS_INFO.email}`} className="flex items-center gap-3 text-cream/40 hover:text-stone/70 transition-colors cursor-pointer">
              <i className="ri-mail-line text-stone/60" />
              <span className="font-sans text-sm">{BUSINESS_INFO.email}</span>
            </a>
            <Link
              to="/location#contact"
              className="mt-4 px-8 py-3.5 bg-cream/10 border border-cream/20 text-cream text-xs tracking-[0.2em] uppercase font-sans text-center rounded-full hover:bg-cream/20 transition-all cursor-pointer whitespace-nowrap"
            >
              Schedule a Consultation
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
